import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  GET_EVENT_CUSTOMIZATION_API,
  GUEST_CHECKIN_COUNT_API,
} from "../Constant/api";
import { useToast } from "../Components/Toast";
import LoadingInComponent from "../Components/LoadingInComponent";
import { loadGoogleFont } from "../Constant/fontLoader";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { DASHBOARD } from "../Router/path";
import Button from "../Components/Button";
import TagCards from "../Components/TagCards";
import { normalizeColor } from "../Constant/colors";

const GuestCounter = () => {
  const [guestCount, setGuestCount] = useState(null);
  const [customization, setCustomization] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [TagCard, setTagCard] = useState([]);

  const rightSide = TagCard.length > 0;

  const fetchGuestCount = useCallback((isInitial = false) => {
    if (isInitial) setLoading(true);

    axios
      .get(GUEST_CHECKIN_COUNT_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((res) => {
        const data = res.data.data;
        setGuestCount(data);
        setTagCard(data.tags || []);

        if (isInitial) setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast("Failed to fetch guest counter.", "error");
        if (isInitial) setLoading(false);
      });
  }, [toast]);

  const fetchCustomizations = useCallback(() => {
    axios
      .get(GET_EVENT_CUSTOMIZATION_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((res) => {
        const data = res.data.data;
        setCustomization(data);

        if (data?.title_font?.family) {
          loadGoogleFont(data.title_font.family);
        }

        if (data?.content_font?.family) {
          loadGoogleFont(data.content_font.family);
        }
      })
      .catch((err) => {
        console.error(err);
        toast("Failed to fetch event customization.", "error");
      });
  }, [toast]);

  useEffect(() => {
    fetchGuestCount(true);
    fetchCustomizations();
    const interval = setInterval(() => {
      fetchGuestCount(false);
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchGuestCount, fetchCustomizations]);

  const percentage = guestCount
    ? Math.round(
        (guestCount.checked_in_guests / guestCount.total_guests) * 100
      ) || 0
    : 0;

  const theme_color = normalizeColor(customization?.theme_color || "#000000");

  return (
    <div
      className={`grid ${
        rightSide ? "grid-cols-2" : "grid-cols-1"
      } w-full h-screen`}
    >
      <LoadingInComponent isLoading={loading}>
        {guestCount && (
          <div
            className={`relative flex flex-col items-center justify-center ${
              rightSide ? "w-full" : "col-span-2 w-full"
            } h-full`}
            style={{
              backgroundImage: `url(${customization?.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              color: theme_color,
            }}
          >
            <Button
              variant="secondary"
              onClick={() => navigate(DASHBOARD)}
              className="absolute top-4 left-4 shadow-lg z-20"
            >
              <XMarkIcon className="w-4 h-4" />
              Exit Page
            </Button>

            <div className="relative z-10 flex flex-col items-center justify-center text-center gap-20 px-8 h-full">
              <h1
                className="text-[72px] font-bold px-8"
                style={{
                  fontFamily: customization?.title_font?.family ?? "inherit",
                }}
              >
                {guestCount.title}
              </h1>

              <div className="bg-white shadow-lg p-6 flex flex-col items-center text-center gap-1 w-full max-w-[500px] mb-16 rounded-md">
                <p className="text-2xl tracking-wide">
                  Guest Check-In Progress
                </p>

                <div className="flex items-baseline -mb-7">
                  <span className="text-[90px] font-bold">
                    {guestCount.checked_in_guests ?? 0}
                  </span>
                  <span className="text-[30px] font-bold">
                    /{guestCount.total_guests ?? 0}
                  </span>
                </div>

                <p className="text-base font-medium tracking-wide">
                  Guests Checked In
                </p>

                <div className="flex items-center gap-4 w-full max-w-[400px] mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: theme_color,
                      }}
                    ></div>
                  </div>
                  <span className="text-xl font-semibold">{percentage}%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </LoadingInComponent>

      {rightSide && (
        <div className="w-full h-full bg-gray-50 text-black p-6 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            {TagCard.map((item) => (
              <TagCards key={item.id} data={item} themeColor={theme_color} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestCounter;
